import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, Box, Paper, TextField, Tooltip, Button, CircularProgress,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { WithContext as ReactTags } from 'react-tag-input';
import { useCreateSmartAlert } from '../hooks/useSmartAlerts';
import { useNotification } from '../context/NotificationContext';
import PortalPicker from './PortalPicker';

const KeyCodes = { comma: 188, enter: 13 };
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const SectionHeader = ({ title, tooltip }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
        <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
        >
            {title}
        </Typography>
        {tooltip && (
            <Tooltip title={tooltip} placement="right">
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
        )}
    </Box>
);

export default function SmartAlertCreate() {
    const navigate = useNavigate();
    const createSmartAlert = useCreateSmartAlert();
    const { showError, showWarning } = useNotification();

    const nameRef = useRef(null);
    useEffect(() => { nameRef.current?.focus(); }, []);
    useEffect(() => { document.title = 'Novi smart alert | Media Scanner'; }, []);

    const [name, setName] = useState('');
    const [focusDescription, setFocusDescription] = useState('');
    const [selectedPortals, setSelectedPortals] = useState([]);
    const [termTags, setTermTags] = useState([]);
    const [excludingTermTags, setExcludingTermTags] = useState([]);
    const [emailTags, setEmailTags] = useState([]);
    const [saving, setSaving] = useState(false);

    const isFormValid = name.trim() && termTags.length > 0 && selectedPortals.length > 0 && emailTags.length > 0;

    const handleSave = async () => {
        if (!name.trim()) { showWarning('Naziv je obavezno polje'); return; }
        if (termTags.length === 0) { showWarning('Dodajte barem jedan pojam za pretragu'); return; }
        if (selectedPortals.length === 0) { showWarning('Odaberite barem jedan portal'); return; }
        if (emailTags.length === 0) { showWarning('Dodajte barem jednu email adresu'); return; }

        setSaving(true);
        createSmartAlert.mutate(
            {
                name: name.trim(),
                focus_description: focusDescription.trim() || null,
                list_portal_ids: selectedPortals.map(p => p.id),
                list_term_names: termTags.map(t => t.text),
                list_excluding_term_names: excludingTermTags.map(t => t.text),
                list_email_addresses: emailTags.map(t => t.text),
            },
            {
                onSuccess: () => navigate('/smartAlerts'),
                onError: (err) => {
                    setSaving(false);
                    if (err.response?.status === 403) {
                        showError('Dosegnut je limit smart alerta.');
                    } else {
                        showError('Greška pri kreiranju. Pokušajte ponovo.');
                    }
                },
            }
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
            <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
                    Novi smart alert
                </Typography>

                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Naziv</Typography>
                    <TextField
                        fullWidth
                        inputRef={nameRef}
                        placeholder="Unesi naziv smart alerta"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <SectionHeader
                        title="Opis konteksta"
                        tooltip="Opišite kontekst pojma kako bi AI bolje razlikovao relevantne članke, npr. 'pratim Krku kao farmaceutsku tvrtku, ne otok'"
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Npr. 'Pratim Krku kao farmaceutsku tvrtku, ne otok Krk'"
                        value={focusDescription}
                        onChange={(e) => setFocusDescription(e.target.value)}
                        size="small"
                    />

                    <SectionHeader title="Pojmovi za pretragu" />
                    <ReactTags
                        tags={termTags}
                        suggestions={[]}
                        delimiters={delimiters}
                        handleDelete={(i) => setTermTags(termTags.filter((_, idx) => idx !== i))}
                        handleAddition={(tag) => setTermTags([...termTags, tag])}
                        inputFieldPosition="bottom"
                        placeholder="Unesi pojam, zatim zarez ili Enter"
                        autocomplete
                        editable
                    />

                    <SectionHeader
                        title="Isključujući pojmovi"
                        tooltip="Članci koji sadrže ove pojmove neće biti uključeni"
                    />
                    <ReactTags
                        tags={excludingTermTags}
                        suggestions={[]}
                        delimiters={delimiters}
                        handleDelete={(i) => setExcludingTermTags(excludingTermTags.filter((_, idx) => idx !== i))}
                        handleAddition={(tag) => setExcludingTermTags([...excludingTermTags, tag])}
                        inputFieldPosition="bottom"
                        placeholder="Unesi pojam za isključivanje, zatim zarez ili Enter"
                        autocomplete
                        editable
                    />

                    <SectionHeader title="Portali" />
                    <PortalPicker value={selectedPortals} onChange={setSelectedPortals} showChips={false} />

                    <SectionHeader title="Email adrese za dostavu" />
                    <ReactTags
                        tags={emailTags}
                        suggestions={[]}
                        delimiters={delimiters}
                        handleDelete={(i) => setEmailTags(emailTags.filter((_, idx) => idx !== i))}
                        handleAddition={(tag) => setEmailTags([...emailTags, tag])}
                        inputFieldPosition="bottom"
                        placeholder="Unesi email adresu, zatim zarez ili Enter"
                        autocomplete
                        editable
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
                        <Button variant="outlined" onClick={() => navigate('/smartAlerts')}>
                            Odustani
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving || !isFormValid}
                            startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                            {saving ? 'Kreiranje...' : 'Kreiraj smart alert'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}
